import React, { useState, useRef, useEffect } from 'react';
import { useChat } from '../contexts/ChatContext';
import { Button } from './ui/button.jsx';
import { Mic, Send, AlertCircle } from 'lucide-react';
import { socketService } from '../services/websocketService.js';

const MessageInput = () => {
  const { sendMessage, isLoading, currentConversation } = useChat();
  const [text, setText] = useState('');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showTimeWarning, setShowTimeWarning] = useState(false);
  const audioChunks = useRef([]);
  const recordingTimerRef = useRef(null);
  const timeWarningRef = useRef(null);

  const MAX_RECORDING_TIME = 60; // 1 minute in seconds

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && text.trim() && !isLoading) {
      sendMessage(text);
      setText('');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setIsTranscribing(true);
      setRecordingTime(0);
      setShowTimeWarning(false);
      
      // Clear timers
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (timeWarningRef.current) {
        clearTimeout(timeWarningRef.current);
        timeWarningRef.current = null;
      }
    }
  };

  const startRecordingTimer = () => {
    setRecordingTime(0);
    setShowTimeWarning(false);
    
    // Start the main recording timer
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;
        
        // Show warning at 50 seconds
        if (newTime === 50 && !showTimeWarning) {
          setShowTimeWarning(true);
        }
        
        // Auto-stop at 60 seconds
        if (newTime >= MAX_RECORDING_TIME) {
          stopRecording();
          return 0;
        }
        
        return newTime;
      });
    }, 1000);

    // Set warning timeout at 50 seconds
    timeWarningRef.current = setTimeout(() => {
      setShowTimeWarning(true);
    }, 50000);
  };

  const handleMicClick = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      setText('');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new window.MediaRecorder(stream);
        setMediaRecorder(recorder);
        audioChunks.current = [];
        
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.current.push(event.data);
          }
        };
        
        recorder.onstop = () => {
          const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
          if (currentConversation?.id && audioBlob.size > 0) {
            socketService.sendAudio(audioBlob, currentConversation.id);
            setIsTranscribing(true);
            setTimeout(() => setIsTranscribing(false), 10000);
          } else {
            setIsTranscribing(false);
          }
          stream.getTracks().forEach(track => track.stop());
        };
        
        recorder.start();
        setIsRecording(true);
        startRecordingTimer();
      } catch (err) {
        alert('Could not access microphone. Please check your browser permissions.');
        setIsRecording(false);
        setIsTranscribing(false);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!isLoading) setIsTranscribing(false);
  }, [isLoading]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (timeWarningRef.current) {
        clearTimeout(timeWarningRef.current);
      }
    };
  }, []);

  return (
      <div className="p-4 border-t flex items-center space-x-2 relative">
        {(isRecording || (isTranscribing && !isRecording)) && (
          <div className="absolute left-1/2 -translate-x-1/2 -top-10 flex items-center gap-2 px-4 py-2 rounded-lg shadow bg-white border z-10 animate-fade-in">
            {isRecording && (
              <>
                <span className="inline-block w-3 h-3 rounded-full bg-red-500 animate-pulse"></span>
                <span className="text-red-600 font-mono text-sm">
                  {formatTime(recordingTime)}
                </span>
                {showTimeWarning && (
                  <div className="flex items-center gap-1 text-amber-600">
                    <AlertCircle className="h-4 w-4" />
                    <span className="text-xs">Recording will stop soon</span>
                  </div>
                )}
              </>
            )}
            {isTranscribing && !isRecording && (
              <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            )}
            <span className={`font-semibold ${isRecording ? 'text-red-600' : 'text-blue-600'}`}>
              {isRecording ? 'Listening...' : 'Transcribing...'}
            </span>
          </div>
        )}
        <input
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Type or speak..."
            disabled={isRecording}
            onKeyDown={handleKeyDown}
            className="flex-1 border rounded px-3 py-2"
        />
        <Button 
          onClick={handleMicClick} 
          variant={isRecording ? 'destructive' : 'outline'}
          className={isRecording ? 'animate-pulse' : ''}
        >
          <Mic />
        </Button>
        <Button onClick={() => { if (text.trim()) { sendMessage(text); setText(''); } }} disabled={!text.trim() || isLoading}>
          <Send />
        </Button>
      </div>
  );
};

export default MessageInput; 